import moment from 'moment';
import {useRouter} from 'next/router';
import {pathOr} from 'ramda';
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import useRoutePaths from '@/src/hooks/useRoutePaths';

import {DIFFICULTY_LEVELS_WITH_LABELS, LANGUAGES} from '../../constants/course';
import {DATE_FORMAT, SERVER_FORMAT} from '../../constants/date';
import {COURSE_PREVIEW_PAGE} from '../../constants/pagesTypes';
import convertSecondsToMinutes from '../../utils/convertSecondsToMinutes';
import getRootServerUrl from '../../utils/getRootServerUrl';
import transformUserData from '../../utils/transformUserData';
import {
  selectCourseDetails,
  setContentCreator,
  setCourse,
  setIsCourseBookmarked,
  setWritingReviewAllowed,
  setWritingReviewNotAllowed as setWritingReviewNotAllowedAction,
  updateRating,
  updateVideoTime,
} from './courseDetailsSlice';

export const getAllCourseDuration = (topics) => {
  const durationInSeconds = topics.reduce((prevVideoValue, currentTopic) => {
    const currentTopicDuration = currentTopic.videos.reduce(
      (prevVideoValue, currentVideo) => {
        return Number(prevVideoValue) + Number(currentVideo.duration);
      },
      0
    );

    return Number(prevVideoValue) + Number(currentTopicDuration);
  }, 0);

  return convertSecondsToMinutes(durationInSeconds);
};

/**
 * @param {array} translation - exit tranlations of the course
 * @param {string} currentSlug - current slug translation
 */
const getAvailableTranslations = (translation, currentSlug) => {
  return translation
    .filter((translation) => translation.slug !== currentSlug)
    .map((translation) => ({
      value: translation.language,
      slug: translation.slug,
    }));
};

const getPreparedTopics = (topics) => {
  return topics.map((topic) => {
    const topicDuration = topic.videos.reduce((previousValue, currentValue) => {
      return Number(currentValue.duration) + Number(previousValue);
    }, 0);

    return {
      id: topic.id,
      title: topic.title,
      videosCount: topic.videos.length,
      description: topic.description,
      files: topic.pdf_attachments,
      links: topic.link_attachments,
      technicalFeature: topic.technical_feature,
      videosDuration: convertSecondsToMinutes(topicDuration),
      videos: topic.videos.map((video) => ({
        stoppageTime: video.time_in_seconds,
        id: video.id,
        previewImage: `${getRootServerUrl()}${video.video_preview_image}`,
        duration: convertSecondsToMinutes(video.duration),
        title: video.title,
        durationInSeconds: video.duration,
        url: `${getRootServerUrl()}${video.video_url}`,
      })),
    };
  });
};

export const getPreparedCourse = (course, slug, locale) => {
  const courseTranslation = course.course_translations.find(
    (translation) => translation.slug === slug
  );

  const firstVideo = pathOr(
    null,
    ['topics', 0, 'videos', 0, 'video_url'],
    courseTranslation
  );

  const courseLanguages = LANGUAGES.filter((exitLanguage) =>
    course.course_translations.some(
      (course) => course.language === exitLanguage.value
    )
  );

  return {
    isPublic: course.is_public,
    courseId: course.id,
    isWritingReviewAllowed: courseTranslation.can_rate,
    isHasComment: courseTranslation.is_rated,
    id: courseTranslation.id,
    isBookmarked: course.is_bookmarked,
    hasCertificate: 'Yes', // @todo ask BE key in future
    languages: courseLanguages,
    title: courseTranslation.title,
    coursePreviewImage: `${getRootServerUrl()}/${
      courseTranslation.preview_image
    }`,
    coursePreviewVideo: firstVideo || courseTranslation.preview_video_url,
    courseRequirements: courseTranslation.requirements,
    courseTargetGroups: courseTranslation.target_group,
    courseDescription: courseTranslation.description,
    updateDate: moment(courseTranslation.updated_at, SERVER_FORMAT).format(
      DATE_FORMAT
    ),
    surgeryDate: pathOr(null, ['surgery_date'], courseTranslation),
    rating: courseTranslation.score,
    ratingCount: courseTranslation.ratings_count,
    availableTranslations: getAvailableTranslations(
      course.course_translations,
      slug
    ),
    difficultyLevel: DIFFICULTY_LEVELS_WITH_LABELS.find(
      (difficultyLevel) => difficultyLevel.value === course.difficulty_level
    ),
    topicsCount: courseTranslation.topics.length,
    duration: getAllCourseDuration(courseTranslation.topics),
    courseCategories: course.categories.map(
      (category) => category[`name_${locale}`]
    ),
    topics: getPreparedTopics(courseTranslation.topics),
    slug: courseTranslation.slug,
    coAuthors: courseTranslation.co_authors || [],
  };
};

const useCourseDetailsActions = () => {
  const dispatch = useDispatch();
  const {
    push,
    query: {slug},
    locale,
  } = useRouter();
  const {SIGN_IN_ROUTE, NOT_FOUND_ROUTE} = useRoutePaths();

  const {course, contentCreator, courses, reviews} =
    useSelector(selectCourseDetails);
  const redirectToLoginPageWithReturn = useCallback(() => {
    push({
      pathname: SIGN_IN_ROUTE.getLink(),
      query: {redirectPage: COURSE_PREVIEW_PAGE, redirectPageId: slug},
    });
  }, [slug]);

  const setCourseDetails = (courseDetails) => {
    if (!courseDetails) {
      return setCourse({});
    }
    const {data} = courseDetails;

    if (!data.length) {
      return push(NOT_FOUND_ROUTE.getLink());
    }

    dispatch(setCourse(getPreparedCourse(data[0], slug, locale)));
    dispatch(
      setContentCreator(transformUserData(pathOr(null, [0, 'creator'], data)))
    );
  };

  const toggleCourseBookmark = (isFollowUpdated) => {
    dispatch(setIsCourseBookmarked(isFollowUpdated));
  };

  const updateStoppageTime = useCallback(
    (videoId, stoppageTime) => {
      if (!Object.prototype.hasOwnProperty.call(course, 'id')) return;

      dispatch(updateVideoTime({videoId, stoppageTime}));
    },
    [course]
  );

  const setWritingReviewNotAllowed = () => {
    dispatch(setWritingReviewNotAllowedAction());
  };

  const handleOnVideoWatched = () => {
    dispatch(setWritingReviewAllowed());
  };

  const updateCourseRating = useCallback(
    (newReviewRating) => {
      const divider = reviews.length ? 2 : 1;
      const updatedRating =
        (Number(course.rating) + Number(newReviewRating)) / divider;

      dispatch(
        updateRating({
          rating: updatedRating,
          ratingCount: course.ratingCount + 1,
        })
      );
    },
    [reviews, course]
  );

  const resetCourse = useCallback(() => {
    dispatch(setCourse({}));
  }, []);

  return {
    course,
    contentCreator,
    courses,
    reviews,
    redirectToLoginPageWithReturn,
    setCourseDetails,
    toggleCourseBookmark,
    setWritingReviewNotAllowed,
    updateStoppageTime,
    handleOnVideoWatched,
    updateCourseRating,
    resetCourse,
  };
};

export default useCourseDetailsActions;
